import { createAction } from '../../utils/reduxUtils';
import { PlayTourParams } from './utils/routerUtils';
import { IPlaybookPage, IStaticMapSize, ITourProgress, TourCode, TourEventType } from '../../models';
import { IAsyncError, ITipReminderRequest, ITourProblemReport } from '../../common';

interface InitTourRequest {
  params: PlayTourParams;
  imageWidth: number;
  staticMapSize: IStaticMapSize;
  restartIfNecessary: boolean;
}

interface OpenPlaybookRequest {
  tourCode: TourCode;
  title: string;
  mainImageUrl: string;
  imageWidth: number;
  staticMapSize: IStaticMapSize;
  purchased: boolean;
}

interface TourProblemReportRequest extends ITourProblemReport {
  onSuccess: () => void;
}

interface TipReminderRequest extends ITipReminderRequest {
  onSuccess: () => void;
}

const initializeTour = {
  request: createAction('@playTour/INITIALIZE_TOUR_REQUEST')<InitTourRequest>,
  success: createAction('@playTour/INITIALIZE_TOUR_SUCCESS')<TourCode>,
  failure: createAction('@playTour/INITIALIZE_TOUR_FAILURE')<IAsyncError>,
  cancel: createAction('@playTour/INITIALIZE_TOUR_CANCEL')<void>,
};

const saveTourProgress = {
  request: createAction('@playTour/SAVE_TOUR_PROGRESS_REQUEST')<TourCode>,
  success: createAction('@playTour/SAVE_TOUR_PROGRESS_SUCCESS')<ITourProgress>,
  failure: createAction('@playTour/SAVE_TOUR_PROGRESS_FAILURE')<IAsyncError>,
  cancel: createAction('@playTour/SAVE_TOUR_PROGRESS_CANCEL')<void>,
};

const tourEvents = {
  log: createAction('@playTour/LOG_TOUR_EVENT')<{
    tourCode: TourCode;
    type: TourEventType;
  }>,
  syncSuccess: createAction('@playTour/SYNC_SUCCESS')<number[]>,
};

const openPlaybook = (request: OpenPlaybookRequest, reset: boolean) => ({
  type: '@playTour/OPEN_PLAYBOOK' as const,
  payload: request,
  meta: { reset },
});
openPlaybook.type = '@playTour/OPEN_PLAYBOOK';

const closePlaybook = createAction('@playTour/CLOSE_PLAYBOOK')<string>;

// Should be triggered when the user navigates away from the play-tour page
const clearCurrentlyPlayedTour = createAction('@playTour/CLEAR_CURRENTLY_PLAYED_TOUR')<void>;

const navigatePage = {
  request: createAction('@playTour/NAVIGATE_PAGE_REQUEST')<{
    params: PlayTourParams;
    direction: 'previous' | 'next';
  }>,
  update: createAction('@playTour/NAVIGATE_PAGE_UPDATE')<{
    tourCode: string;
    nextPage: IPlaybookPage;
  }>,
};

const chooseAnswer = createAction('@playTour/CHOOSE_ANSWER')<{
  answer: string;
  tourCode: string;
  stopIndex: number;
}>;


const increaseAttempts = createAction('@playTour/INCREASE_ATTEMPTS')<{
  tourCode: string;
  stopIndex: number;
}>;

const completeTour = {
  request: createAction('@common/COMPLETE_TOUR_REQUEST')<string>,
  success: createAction('@common/COMPLETE_TOUR_SUCCESS')<string>,
};

const completeOnboarding = createAction('@playTour/COMPLETE_ONBOARDING')<void>;

const sendProblemReportAsync = {
  request: createAction('@playTour/SEND_PROBLEM_REPORT_REQUEST')<TourProblemReportRequest>,
  success: createAction('@playTour/SEND_PROBLEM_REPORT_SUCCESS')<void>,
  failure: createAction('@playTour/SEND_PROBLEM_REPORT_FAILURE')<IAsyncError>,
  cancel: createAction('@playTour/SEND_PROBLEM_REPORT_CANCEL')<void>,
};

const sendTipReminder = {
  request: createAction('@playTour/SEND_TIP_REMINDER_REQUEST')<TipReminderRequest>,
  success: createAction('@playTour/SEND_TIP_REMINDER_SUCCESS')<void>,
  failure: createAction('@playTour/SEND_TIP_REMINDER_FAILURE')<IAsyncError>,
  cancel: createAction('@playTour/SEND_TIP_REMINDER_CANCEL')<void>,
};

const updateScore = createAction('@playTour/UPDATE_SCORE')<{
  tourCode: string;
  score: number;
}>;

const markOnboardingAudioAsPlayed = createAction('@playTour/MARK_ONBOARDING_AUDIO_AS_PLAYED')<void>;

const actions = {
  initializeTour,
  openPlaybook,
  closePlaybook,
  navigatePage,
  chooseAnswer,
  saveTourProgress,
  tourEvents,
  completeTour,
  sendProblemReportAsync,
  sendTipReminder,
  clearCurrentlyPlayedTour,
  completeOnboarding,
  increaseAttempts,
  updateScore,
  markOnboardingAudioAsPlayed,
};

export default actions;
